"use client";

import { Button, Dialog, Select, TextInput } from "@hisani/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  FileCheck2,
  HeartHandshake,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  X,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../auth-client";
import { Link, useRouter } from "../../../i18n/navigation";
import { localizedPath } from "../../../i18n/paths";
import { requestApi, requestApiJson } from "../../api-client";
import { ConversationDialog } from "./conversation-dialog";
import { shareMarketplaceListing } from "./marketplace-browser";
import {
  ConversationSchema,
  EMPTY_FILTERS,
  SortSchema,
  marketplaceFilterError,
  marketplaceFiltersFromSearchParams,
  marketplaceFiltersSearchParams,
  marketplacePriceToMinor,
  type Filters,
} from "./marketplace-contracts";
import { MarketplaceFilterFields } from "./marketplace-filter-fields";
import { breedLabel } from "./marketplace-formatting";
import {
  MarketplaceListingCard,
  MarketplaceListingDetailDialog,
} from "./marketplace-listing";
import { type MarketplacePreviewListing } from "./marketplace-sample-data";
import {
  marketplaceBrowseQueryOptions,
  marketplaceConversationFromPages,
  marketplaceConversationQueryOptions,
  initialMarketplaceConversationData,
  marketplaceQueryKeys,
  updateBrowseListing,
  type MarketplaceBrowseData,
} from "./marketplace-queries";
import {
  MarketplaceEmptyState,
  MarketplaceLoadingGrid,
} from "./marketplace-states";
import { ReportDialog } from "./report-dialog";

const NO_LISTINGS: readonly MarketplacePreviewListing[] = [];

function countMarketplaceFilters(filters: Filters): number {
  return [
    filters.breed,
    filters.sex,
    filters.minimumAge,
    filters.maximumAge,
    filters.minimumPrice,
    filters.maximumPrice,
    filters.location,
    filters.verifiedOnly ? "verified" : "",
  ].filter((value) => value !== "").length;
}

function compareNullablePrice(
  left: string | null,
  right: string | null,
  direction: 1 | -1,
): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  const difference = BigInt(left) - BigInt(right);
  return difference < 0n ? -direction : difference > 0n ? direction : 0;
}

export function MarketplaceWorkspace() {
  const searchParams = useSearchParams();
  const urlFilterKey = searchParams?.toString() ?? "";
  const workspaceKeyParams = new URLSearchParams(urlFilterKey);
  workspaceKeyParams.delete("cursor");
  return (
    <MarketplaceWorkspaceContent
      key={workspaceKeyParams.toString()}
      urlFilterKey={urlFilterKey}
    />
  );
}

function MarketplaceWorkspaceContent({
  urlFilterKey,
}: {
  readonly urlFilterKey: string;
}) {
  const t = useTranslations("Marketplace");
  const locale = useLocale();
  const auth = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const urlFilters = useMemo(
    () => marketplaceFiltersFromSearchParams(new URLSearchParams(urlFilterKey)),
    [urlFilterKey],
  );
  const [filters, setFilters] = useState<Filters>(urlFilters);
  const [draftFilters, setDraftFilters] = useState<Filters>(urlFilters);
  const [filterError, setFilterError] = useState<string | null>(null);
  const browseQuery = useInfiniteQuery(marketplaceBrowseQueryOptions(filters));
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    new URLSearchParams(urlFilterKey).get("listing"),
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationQuery = useInfiniteQuery(
    marketplaceConversationQueryOptions(conversationId),
  );
  const conversation = useMemo(
    () => marketplaceConversationFromPages(conversationQuery.data),
    [conversationQuery.data],
  );
  const [authGateOpen, setAuthGateOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const listings = useMemo(
    () =>
      browseQuery.data?.pages.flatMap((page) => page.listings) ?? NO_LISTINGS,
    [browseQuery.data],
  );
  const mode = browseQuery.isPending
    ? "loading"
    : browseQuery.data === undefined && browseQuery.isError
      ? "error"
      : (browseQuery.data?.pages[0]?.mode ?? "error");
  const selected =
    listings.find((listing) => listing.id === selectedId) ?? null;
  const appliedFilterCount = countMarketplaceFilters(filters);
  const draftFilterCount = countMarketplaceFilters(draftFilters);
  const hasAppliedCriteria =
    appliedFilterCount > 0 || filters.search.trim() !== "";

  const visibleListings = useMemo(() => {
    if (mode !== "sample") return listings;
    const normalizedSearch = filters.search.trim().toLocaleLowerCase(locale);
    const normalizedLocation = filters.location
      .trim()
      .toLocaleLowerCase(locale);
    const minimumPriceMinor =
      filters.minimumPrice === ""
        ? null
        : BigInt(marketplacePriceToMinor(filters.minimumPrice));
    const maximumPriceMinor =
      filters.maximumPrice === ""
        ? null
        : BigInt(marketplacePriceToMinor(filters.maximumPrice));
    const filtered = listings.filter((listing) => {
      const searchable =
        `${listing.horseName} ${breedLabel(listing, t)} ${listing.city} ${listing.countryCode} ${listing.description ?? ""}`.toLocaleLowerCase(
          locale,
        );
      const listingPrice =
        listing.priceMinor === null ? null : BigInt(listing.priceMinor);
      return (
        (normalizedSearch === "" || searchable.includes(normalizedSearch)) &&
        (filters.breed === "" || listing.breed === filters.breed) &&
        (filters.sex === "" || listing.sex === filters.sex) &&
        (filters.minimumAge === "" ||
          listing.ageYears >= Number(filters.minimumAge)) &&
        (filters.maximumAge === "" ||
          listing.ageYears <= Number(filters.maximumAge)) &&
        (minimumPriceMinor === null ||
          (listing.currency === filters.currency &&
            listingPrice !== null &&
            listingPrice >= minimumPriceMinor)) &&
        (maximumPriceMinor === null ||
          (listing.currency === filters.currency &&
            listingPrice !== null &&
            listingPrice <= maximumPriceMinor)) &&
        (normalizedLocation === "" ||
          `${listing.city} ${listing.countryCode}`
            .toLocaleLowerCase(locale)
            .includes(normalizedLocation)) &&
        (!filters.verifiedOnly || listing.verifiedOwner)
      );
    });
    return [...filtered].sort((left, right) => {
      if (filters.sort === "PRICE_ASC")
        return compareNullablePrice(left.priceMinor, right.priceMinor, 1);
      if (filters.sort === "PRICE_DESC")
        return compareNullablePrice(left.priceMinor, right.priceMinor, -1);
      return (
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      );
    });
  }, [filters, listings, locale, mode, t]);

  const requireAuthentication = useCallback(
    (action: () => void) => {
      if (auth.status !== "authenticated") {
        setAuthGateOpen(true);
        return;
      }
      action();
    },
    [auth.status],
  );

  const shareListing = async (listing: MarketplacePreviewListing) => {
    setNotice(null);
    const url = new URL(window.location.href);
    url.searchParams.set("listing", listing.id);
    const result = await shareMarketplaceListing({
      title: t("shareListingTitle", { horseName: listing.horseName }),
      url: url.toString(),
    });
    if (result === "SHARED") setNotice(t("listingShared"));
    if (result === "COPIED") setNotice(t("listingLinkCopied"));
    if (result === "UNAVAILABLE") setNotice(t("shareError"));
  };

  const bookmarkMutation = useMutation({
    mutationFn: async (listing: MarketplacePreviewListing) => {
      await requestApi(`/api/v1/marketplace/listings/${listing.id}/bookmark`, {
        method: listing.bookmarked ? "DELETE" : "PUT",
        headers: { "content-type": "application/json" },
      });
      return { listingId: listing.id, bookmarked: !listing.bookmarked };
    },
    onMutate: async (listing) => {
      setNotice(null);
      await queryClient.cancelQueries({
        queryKey: marketplaceQueryKeys.browse,
      });
      const previous = queryClient.getQueriesData<MarketplaceBrowseData>({
        queryKey: marketplaceQueryKeys.browse,
      });
      queryClient.setQueriesData<MarketplaceBrowseData>(
        { queryKey: marketplaceQueryKeys.browse },
        (current) =>
          updateBrowseListing(current, listing.id, (item) => ({
            ...item,
            bookmarked: !listing.bookmarked,
          })),
      );
      return { previous };
    },
    onError: (_error, _listing, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
      setNotice(t("actionError"));
    },
    onSuccess: ({ bookmarked }) => {
      setNotice(bookmarked ? t("bookmarkSaved") : t("bookmarkRemoved"));
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: marketplaceQueryKeys.browse }),
  });

  const conversationMutation = useMutation({
    mutationFn: (listing: MarketplacePreviewListing) =>
      requestApiJson(
        `/api/v1/marketplace/listings/${listing.id}/conversations`,
        ConversationSchema,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            origin: window.location.origin,
          },
        },
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(
        marketplaceQueryKeys.conversation(conversation.id),
        initialMarketplaceConversationData(conversation),
      );
      setConversationId(conversation.id);
      void queryClient.invalidateQueries({
        queryKey: marketplaceQueryKeys.conversations,
      });
      setNotice(t("conversationReady"));
      setSelectedId(null);
    },
    onError: () => setNotice(t("actionError")),
  });

  const pendingAction =
    bookmarkMutation.isPending || conversationMutation.isPending;

  const toggleBookmark = useCallback(
    (listing: MarketplacePreviewListing) => {
      requireAuthentication(() => {
        bookmarkMutation.mutate(listing);
      });
    },
    [bookmarkMutation, requireAuthentication],
  );

  const startConversation = useCallback(
    (listing: MarketplacePreviewListing) => {
      requireAuthentication(() => {
        conversationMutation.mutate(listing);
      });
    },
    [conversationMutation, requireAuthentication],
  );

  const openCreate = useCallback(() => {
    requireAuthentication(() => router.push("/marketplace/create"));
  }, [requireAuthentication, router]);

  const openManage = useCallback(() => {
    requireAuthentication(() => router.push("/marketplace/manage"));
  }, [requireAuthentication, router]);

  const applyFilters = useCallback(
    (nextFilters: Filters) => {
      const error = marketplaceFilterError(nextFilters);
      if (error !== null) {
        setFilterError(
          error === "AGE_RANGE"
            ? t("ageRangeError")
            : error === "PRICE_RANGE"
              ? t("priceRangeError")
              : t("filterNumberError"),
        );
        return false;
      }
      const firstPageFilters = { ...nextFilters, cursor: "" };
      setFilterError(null);
      setFilters(firstPageFilters);
      setDraftFilters(firstPageFilters);
      const params =
        marketplaceFiltersSearchParams(firstPageFilters).toString();
      router.replace(
        params === "" ? "/marketplace" : `/marketplace?${params}`,
        {
          scroll: false,
        },
      );
      return true;
    },
    [router, t],
  );

  const resetFilters = useCallback(() => {
    applyFilters(EMPTY_FILTERS);
  }, [applyFilters]);

  const loadNextPage = useCallback(async () => {
    const cursor = browseQuery.data?.pages.at(-1)?.nextCursor ?? null;
    if (cursor === null) return;
    const result = await browseQuery.fetchNextPage();
    if (result.isFetchNextPageError) return;
    const params = marketplaceFiltersSearchParams({
      ...filters,
      cursor,
    }).toString();
    router.replace(`/marketplace?${params}`, { scroll: false });
  }, [browseQuery, filters, router]);

  return (
    <div className="bg-canvas">
      <div className="mx-auto max-w-app px-4 pt-0 pb-4 sm:px-6 sm:py-6 lg:px-8">
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            applyFilters(draftFilters);
          }}
          role="search"
        >
          <div className="relative min-w-0 flex-1">
            <span className="sr-only">{t("searchLabel")}</span>
            <Search
              className="pointer-events-none absolute start-3 top-1/2 z-10 size-5 -translate-y-1/2 text-secondary"
              aria-hidden="true"
            />
            <TextInput
              type="search"
              value={draftFilters.search}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              aria-label={t("searchLabel")}
              placeholder={t("searchPlaceholderShort")}
              className="min-h-11 rounded-xl pe-11 ps-10"
            />
            {draftFilters.search === "" ? null : (
              <button
                type="button"
                aria-label={t("clearSearch")}
                className="absolute end-0 top-0 inline-flex size-11 items-center justify-center rounded-e-xl text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
                onClick={() => {
                  const cleared = { ...draftFilters, search: "" };
                  setDraftFilters(cleared);
                  applyFilters(cleared);
                }}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="size-11 min-h-11 shrink-0 p-0"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={t("filters")}
          >
            <SlidersHorizontal className="size-5" aria-hidden="true" />
          </Button>
        </form>

        <nav
          aria-label={t("marketplaceNavigation")}
          className="mt-2 grid min-h-11 grid-cols-2 gap-1 rounded-lg bg-brand-subtle p-1"
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 w-full gap-2 px-2 text-brand-strong hover:bg-transparent active:translate-y-0 active:bg-transparent"
            disabled={pendingAction}
            onClick={openManage}
          >
            <Store className="size-4" aria-hidden="true" />
            {t("myListings")}
          </Button>
          <Button
            type="button"
            variant="accent"
            size="sm"
            className="min-h-11 w-full gap-2 px-2"
            onClick={openCreate}
          >
            <HeartHandshake className="size-4" aria-hidden="true" />
            {t("createListing")}
          </Button>
        </nav>

        {appliedFilterCount === 0 ? null : (
          <div className="mt-2 flex min-h-11 items-center justify-between gap-3 rounded-lg bg-brand-subtle px-2">
            <p className="text-sm font-semibold text-brand-ink">
              {t("filtersApplied", { count: appliedFilterCount })}
            </p>
            {visibleListings.length === 0 ? null : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="min-h-11 px-2 text-brand-strong hover:bg-transparent active:translate-y-0 active:bg-transparent"
                onClick={resetFilters}
              >
                {t("resetFilters")}
              </Button>
            )}
          </div>
        )}

        {mode === "sample" ? (
          <div
            role="status"
            className="mt-3 flex gap-2 border-y border-border-subtle py-2.5 text-xs leading-5 text-brand-ink sm:text-sm"
          >
            <FileCheck2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span className="sm:hidden">{t("sampleNoticeShort")}</span>
            <span className="hidden sm:inline">{t("sampleNotice")}</span>
          </div>
        ) : null}
        {notice === null ? null : (
          <div
            role="status"
            aria-live="polite"
            className="mt-3 border-y border-border-subtle py-3 text-sm font-semibold"
          >
            {notice}
          </div>
        )}

        <div className="mt-3">
          <section aria-labelledby="marketplace-results-heading">
            <h1 id="marketplace-results-heading" className="sr-only">
              {t("availableHorses")}
            </h1>
            <div className="mb-2 flex min-h-11 flex-wrap items-center justify-between gap-2">
              <p className="shrink-0 text-base font-bold text-primary">
                {t("resultCount", { count: visibleListings.length })}
              </p>
              {visibleListings.length === 0 ? null : (
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-sm font-semibold text-secondary">
                    {t("sortLabel")}
                  </span>
                  <Select
                    value={draftFilters.sort}
                    onValueChange={(value) => {
                      const sort = SortSchema.parse(value);
                      applyFilters({ ...draftFilters, sort });
                    }}
                    aria-label={t("sortLabel")}
                    variant="seamless"
                    className="w-auto"
                  >
                    <option value="NEWEST">{t("sortNewest")}</option>
                    <option value="PRICE_ASC">{t("sortPriceAsc")}</option>
                    <option value="PRICE_DESC">{t("sortPriceDesc")}</option>
                  </Select>
                </div>
              )}
            </div>

            {mode === "loading" ? (
              <MarketplaceLoadingGrid label={t("loading")} />
            ) : mode === "error" ? (
              <MarketplaceEmptyState
                title={t("errorTitle")}
                body={t("errorDescription")}
                action={t("retry")}
                onAction={() => void browseQuery.refetch()}
              />
            ) : visibleListings.length === 0 && hasAppliedCriteria ? (
              <MarketplaceEmptyState
                title={t("emptyTitle")}
                body={t("emptyDescription")}
                action={t("resetFilters")}
                onAction={resetFilters}
              />
            ) : visibleListings.length === 0 ? (
              <MarketplaceEmptyState
                title={t("emptyCatalogueTitle")}
                body={t("emptyCatalogueDescription")}
              />
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-2 md:gap-5 min-[1200px]:grid-cols-3">
                  {visibleListings.map((listing) => (
                    <MarketplaceListingCard
                      key={listing.id}
                      listing={listing}
                      locale={locale}
                      pending={pendingAction}
                      onOpen={() => setSelectedId(listing.id)}
                      onBookmark={() => toggleBookmark(listing)}
                      onShare={() => void shareListing(listing)}
                    />
                  ))}
                </div>
                {browseQuery.isFetchNextPageError ? (
                  <div
                    role="alert"
                    className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm font-semibold text-danger"
                  >
                    <span>{t("loadMoreError")}</span>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void loadNextPage()}
                    >
                      {t("retry")}
                    </Button>
                  </div>
                ) : browseQuery.hasNextPage ? (
                  <div className="mt-6 flex justify-center">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={browseQuery.isFetchingNextPage}
                      onClick={() => void loadNextPage()}
                    >
                      {browseQuery.isFetchingNextPage
                        ? t("loadingMore")
                        : t("loadMore")}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
        <section className="mt-6 border-t border-border-subtle pt-4">
          <p className="flex items-start gap-2 text-xs leading-5 text-secondary">
            <ShieldCheck
              className="mt-0.5 size-4 shrink-0 text-brand-strong"
              aria-hidden="true"
            />
            {t("noPayments")}
          </p>
        </section>
      </div>

      <Dialog
        open={filtersOpen}
        title={t("filters")}
        description={t("filtersDescription")}
        closeLabel={t("close")}
        onClose={() => setFiltersOpen(false)}
        footer={
          <>
            <Button
              type="button"
              variant="accent"
              size="lg"
              className="min-h-12 gap-2"
              onClick={() => {
                if (applyFilters(draftFilters)) setFiltersOpen(false);
              }}
            >
              <Search className="size-4" aria-hidden="true" />
              {t("applyFilters")}
            </Button>
            {draftFilterCount === 0 ? null : (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="min-h-12"
                onClick={() => {
                  resetFilters();
                  setFiltersOpen(false);
                }}
              >
                {t("resetFilters")}
              </Button>
            )}
          </>
        }
      >
        <MarketplaceFilterFields
          filters={draftFilters}
          onChange={setDraftFilters}
          {...(filterError === null ? {} : { errorMessage: filterError })}
        />
      </Dialog>

      <MarketplaceListingDetailDialog
        listing={selected}
        locale={locale}
        pending={pendingAction}
        canMessage={
          auth.status !== "authenticated" ||
          selected === null ||
          selected.sellerId !== auth.account.id
        }
        onClose={() => setSelectedId(null)}
        onBookmark={() => selected !== null && toggleBookmark(selected)}
        onMessage={() => selected !== null && startConversation(selected)}
        onReport={() =>
          requireAuthentication(() => {
            setReportOpen(true);
          })
        }
      />

      <ConversationDialog
        open={conversationId !== null}
        title={selected?.horseName ?? null}
        participantContext={
          selected === null || auth.status !== "authenticated"
            ? null
            : selected.sellerId === auth.account.id
              ? t("buyerConversation")
              : t("sellerConversation")
        }
        conversation={conversation}
        accountId={
          auth.status === "authenticated" ? auth.account.id : undefined
        }
        loading={conversationId !== null && conversationQuery.isPending}
        conversationError={
          conversationId !== null &&
          conversationQuery.isError &&
          conversation === null
        }
        hasOlderMessages={conversationQuery.hasNextPage}
        isLoadingOlderMessages={conversationQuery.isFetchingNextPage}
        olderMessagesFailed={conversationQuery.isFetchNextPageError}
        onLoadOlderMessages={() => void conversationQuery.fetchNextPage()}
        onRetry={() => void conversationQuery.refetch()}
        onClose={() => setConversationId(null)}
      />

      <ReportDialog
        listing={selected}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onReported={() => {
          setReportOpen(false);
          setSelectedId(null);
          setNotice(t("reportReceived"));
        }}
      />

      <Dialog
        open={authGateOpen}
        title={t("authTitle")}
        description={t("authDescription")}
        closeLabel={t("close")}
        onClose={() => setAuthGateOpen(false)}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAuthGateOpen(false)}
            >
              {t("continueBrowsing")}
            </Button>
            <Link
              href={`/login?${new URLSearchParams({
                returnTo: localizedPath(
                  locale,
                  urlFilterKey === ""
                    ? "/marketplace"
                    : `/marketplace?${urlFilterKey}`,
                ),
              }).toString()}`}
              className="inline-flex min-h-11 items-center justify-center rounded-md bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              {t("signIn")}
            </Link>
          </>
        }
      />
    </div>
  );
}
